import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowUpRight,
  Copy,
  Check,
  KeyRound,
  LogOut,
  Pencil,
  RefreshCw,
  Smartphone,
  Store,
  Package,
  Bot,
  Undo2,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/fittin/Nav";
import Footer from "@/components/fittin/Footer";
import FitKeyPass from "@/components/fittin/FitKeyPass";
import WalletAdd from "@/components/fittin/WalletAdd";
import FootSlider from "@/components/fittin/FootSlider";
import ComingSoonBadge from "@/components/fittin/ComingSoonBadge";
import FitPreference, { measuredFootLength } from "@/components/fittin/FitPreference";
import { ConfirmFitForm, ReturnForm, StatusChip } from "@/components/fittin/FitKeyActions";
import { passSize } from "@/lib/fitkey";
import { authRequest, clearSession, justSignedOut, useSession } from "@/lib/session";
import { formatSize } from "@/lib/sizes";

const labelCls = "text-[10.5px] uppercase tracking-[0.2em] text-neutral-500";
const cardCls = "rounded-3xl border border-black/10 bg-white p-6 sm:p-7";
const GENDER_LABEL = { men: "Men", women: "Women", unisex: "Unisex" };
const GENDERS = ["men", "women", "unisex"];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (_e) {
    return "";
  }
};

// /me — My FitKey (signed in with the Wallet key only).
export default function Me() {
  const session = useSession();
  const [me, setMe] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error

  const load = useCallback(async () => {
    try {
      const data = await authRequest("get", "/me");
      setMe(data);
      setState("ok");
    } catch (e) {
      if (e.status !== 401) setState("error");
    }
  }, []);

  useEffect(() => {
    if (session) load();
  }, [session, load]);

  if (!session) return <Navigate to={justSignedOut() ? "/" : "/signin?next=%2Fme"} replace />;

  const signOut = () => {
    clearSession({ userInitiated: true });
    toast("Signed out on this device. Your FitKey stays in your Wallet.");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <Nav />
      <main data-testid="me-page" className="mx-auto max-w-7xl px-6 md:px-10 pt-28 md:pt-32 pb-24">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
              <span className="h-px w-8 bg-black/40" />
              My FitKey
            </div>
            <h1 data-testid="me-headline" className="mt-4 text-4xl sm:text-5xl tracking-[-0.035em] font-medium leading-[1.03]">
              Your FitKey. <span className="text-neutral-400">Your size, your rules.</span>
            </h1>
          </div>
          <button
            type="button"
            data-testid="me-signout"
            onClick={signOut}
            className="self-start sm:self-auto inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-4 py-2 text-[13px] hover:bg-neutral-50 transition-all"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out on this device
          </button>
        </div>

        {state === "loading" && (
          <div data-testid="me-loading" className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 h-[420px] rounded-[28px] bg-neutral-200/60 animate-pulse" />
            <div className="lg:col-span-8 h-[420px] rounded-3xl bg-neutral-200/40 animate-pulse" />
          </div>
        )}

        {state === "error" && (
          <div data-testid="me-error" className={`mt-12 ${cardCls} flex flex-wrap items-center gap-3 text-[14px]`}>
            We couldn’t load your FitKey right now.
            <button
              type="button"
              data-testid="me-retry"
              onClick={() => {
                setState("loading");
                load();
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white px-4 py-2 text-[12.5px]"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Try again
            </button>
          </div>
        )}

        {state === "ok" && me && (
          <>
            <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
              <div className="lg:col-span-4">
                <div className="w-full max-w-[340px] mx-auto lg:mx-0">
                  <FitKeyPass
                    size={passSize(me)}
                    brand={me.reference_brand}
                    model={me.reference_model}
                    fitkeyId={me.fitkey_id}
                    testid="me-pass"
                  />
                  <WalletAdd googleUrl={me.google_wallet_url} appleUrl={me.apple_wallet_url} testid="me-wallet" className="mt-5" />
                </div>
              </div>
              <div className="lg:col-span-8 space-y-6">
                <ProfileCard me={me} onSaved={setMe} />
                <PairDevice />
              </div>
            </div>

            <ActivityCard me={me} onChanged={load} />

            <div className="mt-14">
              <div className="flex items-center gap-3">
                <h2 className="text-[22px] font-medium tracking-tight">Connect more</h2>
                <ComingSoonBadge testid="me-connect-coming-soon" />
              </div>
              <p className="mt-1.5 text-[13.5px] text-neutral-500">Your FitKey, in the places you shop. You stay in control.</p>
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <ConnectedShops />
                <OrderHistory />
                <McpConnect />
              </div>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

/* ---------------------------------------------------------------- */
/* Profile: reference shoe, size, measurements, fit preference       */
/* ---------------------------------------------------------------- */
function Row({ label, value, testid, muted }) {
  return (
    <div className="py-3 flex items-baseline justify-between gap-4 border-b border-black/5 last:border-0">
      <span className={labelCls}>{label}</span>
      <span data-testid={testid} className={`text-[14px] text-right ${muted ? "text-neutral-400" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function ProfileCard({ me, onSaved }) {
  const measured = measuredFootLength(me);
  const [editing, setEditing] = useState(false);
  const [len, setLen] = useState(measured);
  const [wid, setWid] = useState(me.foot_width_cm ?? null);
  const [gender, setGender] = useState(me.gender || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const startEdit = () => {
    setLen(measured);
    setWid(me.foot_width_cm ?? null);
    setGender(me.gender || null);
    setError("");
    setEditing(true);
  };

  const save = async () => {
    const body = {};
    if (len !== null && len !== measured) body.foot_length_cm = len;
    if (wid !== null && wid !== (me.foot_width_cm ?? null)) body.foot_width_cm = wid;
    if (gender && gender !== me.gender) body.gender = gender;
    if (!Object.keys(body).length) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const data = await authRequest("patch", "/me", body);
      onSaved(data);
      setEditing(false);
      toast("Your FitKey is updated.");
    } catch (e) {
      if (e.status !== 401) setError("Couldn’t save your changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const size = passSize(me);
  const refSize = me.reference_size ? `${me.reference_size_system || "EU"} ${formatSize(me.reference_size)}` : "—";

  return (
    <section data-testid="me-profile" className={cardCls}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[19px] font-medium tracking-tight">Profile</h2>
        {!editing && (
          <button
            type="button"
            data-testid="me-profile-edit"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3.5 py-1.5 text-[12.5px] hover:bg-neutral-50"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit measurements
          </button>
        )}
      </div>

      {!editing ? (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 md:gap-x-10">
          <div>
            <Row
              label="Reference shoe"
              testid="me-reference-shoe"
              value={[me.reference_brand, me.reference_model].filter(Boolean).join(" ") || "—"}
            />
            <Row label="Size you wear" testid="me-reference-size" value={refSize} />
            <Row label="Your FitKey size" testid="me-size" value={size.value === "—" ? "—" : `${size.unit} ${size.value}`} />
          </div>
          <div>
            <Row
              label="Foot length"
              testid="me-foot-length"
              value={measured != null ? `${measured.toFixed(1)} cm` : "Not measured"}
              muted={measured == null}
            />
            <Row
              label="Foot width"
              testid="me-foot-width"
              value={me.foot_width_cm != null ? `${Number(me.foot_width_cm).toFixed(1)} cm` : "Not measured"}
              muted={me.foot_width_cm == null}
            />
            <Row
              label="Fit"
              testid="me-gender"
              value={GENDER_LABEL[me.gender] || "Not set"}
              muted={!me.gender}
            />
          </div>
          <div className="md:col-span-2 mt-4">
            <FitPreference fitkey={me} testid="me-fit-preference" />
          </div>
        </div>
      ) : (
        <div data-testid="me-profile-form" className="mt-6 space-y-7">
          <FootSlider
            label="Foot length"
            min={18}
            max={33}
            restAt={measured ?? 26}
            value={len}
            onChange={setLen}
            clearable={false}
            hint="Heel to longest toe"
            testid="me-edit-foot-length"
          />
          <FootSlider
            label="Foot width"
            min={7}
            max={13}
            restAt={me.foot_width_cm ?? 10}
            value={wid}
            onChange={setWid}
            clearable={false}
            hint="Widest point"
            testid="me-edit-foot-width"
          />
          <div className="flex flex-wrap items-center gap-2">
            <span className={`${labelCls} mr-1`}>Fit</span>
            {GENDERS.map((g) => (
              <button
                key={g}
                type="button"
                data-testid={`me-edit-gender-${g}`}
                aria-pressed={gender === g}
                onClick={() => setGender(g)}
                className={`rounded-full border px-3 py-1.5 text-[12.5px] transition-all ${
                  gender === g ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/10 hover:bg-neutral-50"
                }`}
              >
                {GENDER_LABEL[g]}
              </button>
            ))}
          </div>
          {error && (
            <div data-testid="me-profile-error" className="text-[12px] text-red-600">
              {error}
            </div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="me-profile-save"
              disabled={saving}
              onClick={save}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white h-11 px-5 text-[13px] hover:bg-neutral-800 disabled:opacity-60"
            >
              {saving ? "Saving" : "Save"}
            </button>
            <button
              type="button"
              data-testid="me-profile-cancel"
              onClick={() => setEditing(false)}
              className="px-3 h-11 text-[13px] text-neutral-500 hover:text-[#0A0A0A]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Sign in on another device: one-time code, 5 minutes               */
/* ---------------------------------------------------------------- */
function PairDevice() {
  const [code, setCode] = useState(null);
  const [expiresAt, setExpiresAt] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!code) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [code]);

  const create = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await authRequest("post", "/me/pair");
      setCode(d.code);
      setExpiresAt(Date.now() + (d.expires_in || 300) * 1000);
      setNow(Date.now());
      setCopied(false);
    } catch (e) {
      if (e.status !== 401) setError("Couldn’t create a code right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const left = Math.max(0, Math.round((expiresAt - now) / 1000));
  const expired = code && left === 0;
  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch (_e) {
      toast("Couldn’t copy. Select the code and copy it instead.");
    }
  };

  return (
    <section data-testid="me-pair" className={cardCls}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Smartphone className="h-4 w-4" />
            <h2 className="text-[19px] font-medium tracking-tight">Sign in on another device</h2>
          </div>
          <p className="mt-1.5 max-w-md text-[13px] text-neutral-500 leading-relaxed">
            Create a one-time code, then enter it at getfittin.com/signin on the other device.
          </p>
        </div>
      </div>

      {code && !expired ? (
        <div data-testid="me-pair-code-box" className="mt-5 rounded-2xl bg-[#0A0A0A] text-white p-5 sm:p-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">Your code</div>
              <div data-testid="me-pair-code" className="mt-2 font-mono text-[36px] sm:text-[44px] tracking-[0.14em] leading-none">
                {code}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">Expires in</div>
              <div data-testid="me-pair-countdown" className="mt-2 font-mono text-[22px] tabular-nums">
                {mmss}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="me-pair-copy"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3.5 py-1.5 text-[12px] hover:bg-white/15"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied" : "Copy code"}
            </button>
            <span className="text-[11.5px] text-white/50">Works once. Only share it with yourself.</span>
          </div>
        </div>
      ) : (
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="me-pair-create"
            disabled={loading}
            onClick={create}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white h-11 px-5 text-[13px] hover:bg-neutral-800 disabled:opacity-60"
          >
            <KeyRound className="h-3.5 w-3.5" />
            {loading ? "Creating code" : expired ? "Create a new code" : "Create a sign-in code"}
          </button>
          {expired && (
            <span data-testid="me-pair-expired" className="text-[12.5px] text-neutral-500">
              That code has expired.
            </span>
          )}
        </div>
      )}
      {error && (
        <div data-testid="me-pair-error" className="mt-2 text-[12px] text-red-600">
          {error}
        </div>
      )}
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* Returns & feedback                                                */
/* ---------------------------------------------------------------- */
function ActivityCard({ me, onChanged }) {
  const [open, setOpen] = useState(null); // null | "return" | "fit"
  const returns = Array.isArray(me.returns) ? me.returns : [];
  const feedback = Array.isArray(me.feedback) ? me.feedback : [];
  const size = passSize(me);

  const toggle = (k) => setOpen((cur) => (cur === k ? null : k));
  // Close the finished panel after a moment, unless the user switched panels.
  const doneFor = (k) => () => {
    onChanged();
    setTimeout(() => setOpen((cur) => (cur === k ? null : cur)), 1800);
  };

  return (
    <section data-testid="me-activity" className={`mt-6 ${cardCls}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-[19px] font-medium tracking-tight">Returns & feedback</h2>
          <p className="mt-1 text-[13px] text-neutral-500">Every answer makes your next size sharper.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="me-open-return"
            aria-expanded={open === "return"}
            onClick={() => toggle("return")}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[13px] transition-all ${
              open === "return" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/10 hover:bg-neutral-50"
            }`}
          >
            <Undo2 className="h-3.5 w-3.5" /> Request a return
          </button>
          <button
            type="button"
            data-testid="me-open-fit"
            aria-expanded={open === "fit"}
            onClick={() => toggle("fit")}
            className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[13px] transition-all ${
              open === "fit" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/10 hover:bg-neutral-50"
            }`}
          >
            <MessageSquare className="h-3.5 w-3.5" /> Did it fit?
          </button>
        </div>
      </div>

      {open === "return" && (
        <div className="mt-5 rounded-2xl border border-black/5 bg-neutral-50/60 p-5">
          <ReturnForm key="return" fitkeyId={me.fitkey_id} testid="me-return" onDone={doneFor("return")} />
        </div>
      )}
      {open === "fit" && (
        <div className="mt-5 rounded-2xl border border-black/5 bg-neutral-50/60 p-5">
          <ConfirmFitForm
            key="fit"
            fitkeyId={me.fitkey_id}
            brand={me.reference_brand || ""}
            model=""
            sizeRecommended={size.value !== "—" ? `${size.unit} ${size.value}` : undefined}
            editablePair
            testid="me-fit"
            onDone={doneFor("fit")}
          />
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className={labelCls}>Returns</div>
          {returns.length ? (
            <ul data-testid="me-returns-list" className="mt-2 divide-y divide-black/5">
              {returns.map((r) => (
                <li key={r.return_id || r.created_at} data-testid={`me-return-${r.return_id}`} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[14px] truncate">{r.reason}</div>
                    <div className="text-[11.5px] text-neutral-400">{fmtDate(r.created_at)}</div>
                  </div>
                  <StatusChip status={r.status} testid={`me-return-status-${r.return_id}`} />
                </li>
              ))}
            </ul>
          ) : (
            <div data-testid="me-returns-empty" className="mt-2 rounded-xl border border-dashed border-black/10 px-4 py-5 text-[13px] text-neutral-500">
              No returns yet.
            </div>
          )}
        </div>
        <div>
          <div className={labelCls}>Fit feedback</div>
          {feedback.length ? (
            <ul data-testid="me-feedback-list" className="mt-2 divide-y divide-black/5">
              {feedback.map((f) => (
                <li key={f.id || f.created_at} data-testid={`me-feedback-${f.id}`} className="py-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[14px] truncate capitalize">
                      {[String(f.brand || "").replace(/-/g, " "), f.model].filter(Boolean).join(" ")}
                    </div>
                    <div className="text-[11.5px] text-neutral-400">
                      {[f.size_purchased ? `Bought ${f.size_purchased}` : null, fmtDate(f.created_at)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <StatusChip status={f.fit_verdict} testid={`me-feedback-verdict-${f.id}`} />
                </li>
              ))}
            </ul>
          ) : (
            <div data-testid="me-feedback-empty" className="mt-2 rounded-xl border border-dashed border-black/10 px-4 py-5 text-[13px] text-neutral-500">
              No fit feedback yet.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- */
/* UI shells (endpoints not built yet; no network calls)             */
/* ---------------------------------------------------------------- */
const SHOPS = ["Zalando", "About You"];

function ShellCard({ icon: Icon, title, children, testid }) {
  return (
    <section data-testid={testid} className={`${cardCls} flex flex-col`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="grid place-items-center h-9 w-9 rounded-xl bg-neutral-100">
            <Icon className="h-4 w-4 text-neutral-600" />
          </span>
          <h3 className="text-[16px] font-medium tracking-tight">{title}</h3>
        </div>
        <ComingSoonBadge testid={`${testid}-coming-soon`} />
      </div>
      {children}
    </section>
  );
}

function ConnectedShops() {
  const [asked, setAsked] = useState(null);
  return (
    <ShellCard icon={Store} title="Connected shops" testid="me-shops">
      <p className="mt-3 text-[13px] text-neutral-500 leading-relaxed">
        Connect a shop account and your FitKey size shows up there, without retyping it.
      </p>
      <ul className="mt-4 divide-y divide-black/5">
        {SHOPS.map((s) => {
          const slug = s.toLowerCase().replace(/\s+/g, "-");
          return (
            <li key={s} className="py-3 flex items-center justify-between gap-3">
              <span className="text-[14px]">{s}</span>
              <button
                type="button"
                data-testid={`me-shop-connect-${slug}`}
                onClick={() => setAsked(s)}
                className="rounded-full border border-black/10 bg-white px-3.5 py-1.5 text-[12px] text-neutral-500 hover:bg-neutral-50"
              >
                {asked === s ? "Coming soon" : "Connect"}
              </button>
            </li>
          );
        })}
        <li className="py-3 text-[12.5px] text-neutral-400">More shops to follow.</li>
      </ul>
    </ShellCard>
  );
}

function OrderHistory() {
  return (
    <ShellCard icon={Package} title="Order history" testid="me-orders">
      <div data-testid="me-orders-empty" className="mt-4 flex-1 grid place-items-center rounded-2xl border border-dashed border-black/10 px-5 py-10 text-center">
        <div>
          <Package className="mx-auto h-6 w-6 text-neutral-300" />
          <div className="mt-3 text-[14px]">No orders yet</div>
          <p className="mt-1 text-[12.5px] text-neutral-500 leading-relaxed max-w-[240px] mx-auto">
            Orders from connected shops will appear here, each with the size you kept.
          </p>
        </div>
      </div>
    </ShellCard>
  );
}

const MCP_PLACEHOLDER = `{
  "mcpServers": {
    "fittin": {
      "url": "<coming soon>",
      "token": "<your FitKey assistant token>"
    }
  }
}`;

function McpConnect() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_PLACEHOLDER);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_e) {
      toast("Couldn’t copy. Select the text and copy it instead.");
    }
  };
  return (
    <ShellCard icon={Bot} title="Connect your AI assistant (MCP)" testid="me-mcp">
      <ol className="mt-4 space-y-2.5 text-[13px] text-neutral-600">
        {[
          "Open your AI assistant’s settings and add a new MCP server.",
          "Paste the config below. Your personal token will appear here.",
          "Ask: “Which size should I get in this shoe?”",
        ].map((t, i) => (
          <li key={t} className="flex gap-2.5">
            <span className="grid place-items-center h-5 w-5 shrink-0 rounded-full border border-black/10 text-[10.5px] text-neutral-500">
              {i + 1}
            </span>
            {t}
          </li>
        ))}
      </ol>
      <div className="mt-4 relative rounded-2xl bg-[#0A0A0A] text-white/85">
        <div className="flex items-center justify-between px-4 pt-3 text-[10px] uppercase tracking-[0.22em] text-white/45">
          Placeholder
          <button
            type="button"
            data-testid="me-mcp-copy"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[10.5px] normal-case tracking-normal text-white/80 hover:bg-white/15"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <pre data-testid="me-mcp-config" className="px-4 pb-4 pt-2 font-mono text-[11.5px] leading-[1.65] overflow-x-auto">
          {MCP_PLACEHOLDER}
        </pre>
      </div>
      <Link
        to="/#mcp-teaser"
        data-testid="me-mcp-learn"
        className="mt-4 inline-flex items-center gap-1 text-[12.5px] text-neutral-500 hover:text-[#0A0A0A]"
      >
        See how it works <ArrowUpRight className="h-3.5 w-3.5" />
      </Link>
    </ShellCard>
  );
}
